import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import { FormattedMessage } from 'react-intl';
import { SalesStageType } from 'library/types';
import { GridRowId } from '@mui/x-data-grid-premium';
import { ChangeEvent, FormEvent, useCallback, useEffect, useState } from 'react';
import { Box, Button, TextField, DialogTitle, DialogContentText } from '@mui/material';
import { LoadingBackdrop, ResponsiveDialog, ResponsiveDialogActions, ResponsiveDialogContent } from 'components';

type UpdateSalesStageTypeDialogProps = {
  open: boolean;
  salesStageTypeId: GridRowId;
  handleClose: () => void;
  refresh: () => Promise<void>;
};

const UpdateSalesStageTypeDialog = ({
  open,
  salesStageTypeId,
  handleClose: closeDialog,
  refresh,
}: UpdateSalesStageTypeDialogProps) => {
  // other hooks
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [title, setTitle] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  // async helper functions
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!title) {
      setError(true);
      return;
    }

    const postData: SalesStageType = {
      id: salesStageTypeId as number,
      title,
    };

    try {
      setLoading(true);

      const response = await apiService.updateSalesStageType(postData);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="salesStageTypes.updateSalesStageTypeCompleted"
            defaultMessage="Satış aşaması düzenleme işlemi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );
        handleClose();
        refresh();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const getSalesStageTypeInfos = useCallback(async () => {
    try {
      setLoading(true);

      const response = await apiService.getSpecificSalesStageType(salesStageTypeId as number);

      if (response.isSuccess) {
        setTitle(response.data.title);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [salesStageTypeId]);

  // sync helper functions
  const handleClose = () => {
    closeDialog();
    setTitle('');
    setError(false);
  };

  const onChangeHandler = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setTitle(event.target.value);

  // useEffect hooks
  useEffect(() => {
    if (title) {
      setError(false);
    }
  }, [title]);

  useEffect(() => {
    if (open && salesStageTypeId) {
      getSalesStageTypeInfos();
    }
  }, [open, salesStageTypeId, getSalesStageTypeInfos]);

  return (
    <ResponsiveDialog maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage id="salesStageTypes.updateSalesStageType" defaultMessage="Satış Aşaması Düzenle" />
      </DialogTitle>
      <Box component="form" onSubmit={onSubmit} noValidate>
        <ResponsiveDialogContent>
          <DialogContentText>
            <FormattedMessage
              id="salesStageTypes.updateSalesStageTypeText"
              defaultMessage="İlgili alanları değiştirip devam ederek seçili satış aşaması için düzenleme yapabilirsiniz."
            />
          </DialogContentText>
          <TextField
            label={<FormattedMessage id="salesStageTypes.title" defaultMessage="Adı" />}
            fullWidth
            size="small"
            onChange={onChangeHandler}
            value={title}
            error={error}
            helperText={
              error && <FormattedMessage id="validations.required" defaultMessage="Bu alan boş bırakılamaz" />
            }
          />
        </ResponsiveDialogContent>
        <ResponsiveDialogActions>
          <Button onClick={handleClose}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>
          <Button variant="contained" type="submit">
            <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
          </Button>
        </ResponsiveDialogActions>
      </Box>
      <LoadingBackdrop open={loading} />
    </ResponsiveDialog>
  );
};

export default UpdateSalesStageTypeDialog;
